import { useCallback, useEffect, useState } from 'react';
import { printingApi } from '@verlo/shared/services/api';
import { Notice } from '@verlo/shared/components/ui/notice';
import { Button } from '@verlo/shared/components/ui/button';
import { toast } from 'sonner';

/**
 * A printer has stopped taking tickets (#94).
 *
 * This is the one printing failure that matters: a kitchen ticket that silently did not
 * print means the food is never made, and nobody finds out until the guest asks. So the
 * till says so, on every screen, until someone has dealt with it — and offers the one
 * action that fixes it once the paper is back in.
 *
 * Nothing is shown while printing is working, and nothing is shown at a venue with no
 * printers at all: an absent machine is a venue that has not set one up, not a fault.
 */
const POLL_MS = 20000;

const PrintingBanner = () => {
  const [down, setDown] = useState([]);
  const [stuck, setStuck] = useState([]);
  const [retrying, setRetrying] = useState(false);

  const read = useCallback(() => {
    printingApi.status()
      .then(({ data }) => {
        setDown((data.printers || []).filter((p) => p.state === 'down'));
        // `needs_attention` is the stuck jobs themselves, queried by status. This used to
        // sift them out of `recent` — the last twenty jobs of any status — so in a service
        // where the bar kept printing, the kitchen's dead tickets dropped out within
        // minutes and the banner sat there with nothing to press (#116).
        setStuck(data.needs_attention || []);
      })
      .catch(() => { /* the till is offline from its own API: its own error, not ours */ });
  }, []);

  useEffect(() => {
    read();
    const t = setInterval(read, POLL_MS);
    return () => clearInterval(t);
  }, [read]);

  // Something to say when a printer is down *or* when tickets are owed by one that has
  // since recovered — a reprint from the KDS leaves the dead jobs dead, and they are still
  // tickets somebody never got.
  if (down.length === 0 && stuck.length === 0) return null;

  const again = async () => {
    setRetrying(true);
    try {
      await Promise.all(stuck.map((j) => printingApi.retry(j.id)));
      toast.success(stuck.length === 1 ? 'Sent again' : `${stuck.length} tickets sent again`);
      setTimeout(read, 3000);
    } catch {
      toast.error('Could not send them again');
    } finally { setRetrying(false); }
  };

  const dismiss = async () => {
    setRetrying(true);
    try {
      await Promise.all(stuck.map((j) => printingApi.dismiss(j.id)));
      toast.success('Cleared');
      setTimeout(read, 1000);
    } catch {
      toast.error('Could not clear them');
    } finally { setRetrying(false); }
  };

  const names = down.map((p) => p.name).join(' and ');
  const count = stuck.length;

  return (
    <div className="mb-4" data-testid="printing-banner">
      <Notice
        tone={down.length ? 'stop' : 'warn'}
        word="Printing"
        title={down.length
          ? `${names} ${down.length === 1 ? 'is' : 'are'} not printing`
          : `${count} ticket${count === 1 ? '' : 's'} never printed`}
        action={count > 0 && (
          <span className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={again} disabled={retrying} data-testid="printing-retry">
              {retrying ? 'Sending…' : `Send ${count === 1 ? 'it' : `all ${count}`} again`}
            </Button>
            {/* For the common case: the pass has already reprinted it from the kitchen
                screen, and pressing "Send again" would print an unmarked duplicate of a
                ticket nobody wants. */}
            <Button size="sm" variant="ghost" onClick={dismiss} disabled={retrying} data-testid="printing-dismiss">
              Already dealt with
            </Button>
          </span>
        )}
      >
        {down.length
          ? `${count || 'Some'} ticket${count === 1 ? '' : 's'} could not be delivered. Check the paper and the cable, then send again. `
          : 'The printer is working again, but these were never delivered. '}
        {down[0]?.last_error ? `The printer said: ${down[0].last_error}.` : null}
        {' '}Tickets are still on the kitchen screen.
      </Notice>
    </div>
  );
};

export default PrintingBanner;
